import React from 'react';
import type { OrgFeatureFlagKey } from '../../utils/orgFeatureFlagKeys';
import type { IntegrationConfig, IntegrationSettings } from '../../services/integrationApi';
import LightspeedSetupChecklist from './LightspeedSetupChecklist';
import ShopifyDescriptionTemplateSection from '../ShopifyDescriptionTemplateSection';
import LightspeedRunSummarySection from './LightspeedRunSummarySection';
import { LightspeedKeepFieldsSection, LightspeedRebateDiscountsSection } from './LightspeedSettingsSections';
import { LightspeedDimensionsSection } from './LightspeedDimensionsSection';
import LightspeedTagModeSection from './LightspeedTagModeSection';
import LightspeedPhotoLimitSection from './LightspeedPhotoLimitSection';
import LightspeedSyncEstimateSection from './LightspeedSyncEstimateSection';

export interface LightspeedSettingsSectionProps {
  integration: IntegrationConfig;
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
}

export interface LightspeedSettingsSection {
  id: string;
  /** The organization flag that must be on for the section to show. */
  flag?: OrgFeatureFlagKey;
  /** Extra gate beyond `flag`, given the same flag reader the drawer uses. */
  visible?: (isEnabled: (key: OrgFeatureFlagKey) => boolean) => boolean;
  Component: React.ComponentType<LightspeedSettingsSectionProps>;
}

const SetupChecklist: React.FC<LightspeedSettingsSectionProps> = ({ integration }) => (
  <LightspeedSetupChecklist integrationId={integration.id} />
);

const KeepFields: React.FC<LightspeedSettingsSectionProps> = ({ settings, onChange }) => (
  <LightspeedKeepFieldsSection settings={settings} onChange={onChange} />
);

const RebateDiscounts: React.FC<LightspeedSettingsSectionProps> = ({ integration, settings, onChange }) => (
  <LightspeedRebateDiscountsSection
    integration={integration}
    rebateDiscounts={settings.rebateDiscounts}
    onRebateDiscountsChange={(rebateDiscounts) => onChange({ rebateDiscounts })}
  />
);

const DescriptionTemplate: React.FC<LightspeedSettingsSectionProps> = ({ integration, settings, onChange }) => (
  <ShopifyDescriptionTemplateSection
    settings={settings}
    onChange={onChange}
    integrationId={integration.id}
    flagKey="lightspeed_description_template"
    plainHtml
    subtitleKey="integrations.descriptionTemplate.lightspeedSubtitle"
  />
);

/**
 * The Lightspeed sections the settings drawer mounts, in render order. A later slice appends an
 * entry here (id, flag, component) instead of editing `IntegrationSettingsDrawer`. The drawer
 * shows a section only for a Lightspeed integration whose `flag` (and `visible`) pass.
 */
export const LIGHTSPEED_SETTINGS_SECTIONS: LightspeedSettingsSection[] = [
  { id: 'keep-fields', flag: 'lightspeed_store_config', Component: KeepFields },
  {
    id: 'rebate-discounts',
    flag: 'lightspeed_sales_sync',
    // The register-discount switch ships with either Lightspeed configuration slice.
    visible: (isEnabled) => isEnabled('lightspeed_store_config') || isEnabled('lightspeed_catalog_parity'),
    Component: RebateDiscounts,
  },
  { id: 'setup-checklist', flag: 'lightspeed_setup_checklist', Component: SetupChecklist },
  { id: 'description-template', flag: 'lightspeed_description_template', Component: DescriptionTemplate },
  { id: 'dimensions', flag: 'lightspeed_item_dimensions', Component: LightspeedDimensionsSection },
  { id: 'photo-limit', flag: 'lightspeed_photo_limit', Component: LightspeedPhotoLimitSection },
  { id: 'sync-estimate', flag: 'lightspeed_sync_estimates', Component: LightspeedSyncEstimateSection },
  { id: 'tag-mode', flag: 'lightspeed_catalog_tags', Component: LightspeedTagModeSection },
  { id: 'run-summary', flag: 'lightspeed_sync_run_summary', Component: LightspeedRunSummarySection },
];

/** Whether a section shows for the organization (its `flag`, then its `visible` gate). */
export const isLightspeedSectionShown = (
  section: LightspeedSettingsSection,
  isEnabled: (key: OrgFeatureFlagKey) => boolean,
): boolean => (!section.flag || isEnabled(section.flag)) && (!section.visible || section.visible(isEnabled));
